import {
  CHALLENGE_BANDS_ORDERED,
  CHALLENGE_BAND_INFO,
  MAX_CHALLENGE_MESSAGE_LENGTH,
  type ChallengeBand,
  type ChallengeCandidate,
  type Format,
} from '@foosball/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiError } from '../api/client';
import { Button, Card, FormError } from '../components/ui';
import { useChallengeCandidates, useCreateChallenge } from '../hooks/useChallenges';

function BandPicker({
  buckets,
  selected,
  onSelect,
}: {
  buckets: { band: ChallengeBand; label: string; blurb: string; count: number }[];
  selected: ChallengeBand | null;
  onSelect: (band: ChallengeBand) => void;
}) {
  const counts = new Map(buckets.map((b) => [b.band, b.count]));

  return (
    <div role="radiogroup" aria-label="How hard a game do you want?" className="space-y-2">
      {CHALLENGE_BANDS_ORDERED.map((band) => {
        const info = CHALLENGE_BAND_INFO[band];
        const count = counts.get(band) ?? 0;
        const isSelected = selected === band;

        return (
          <button
            key={band}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={count === 0}
            onClick={() => onSelect(band)}
            className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition
              disabled:cursor-not-allowed disabled:opacity-40
              ${
                isSelected
                  ? 'border-emerald-500 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-950'
                  : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900'
              }`}
          >
            <span>
              <span className="block text-sm font-semibold">{info.label}</span>
              <span className="block text-xs text-slate-500 dark:text-slate-400">{info.blurb}</span>
            </span>
            <span className="ml-3 shrink-0 text-xs tabular-nums text-slate-400">
              {count === 0 ? 'nobody' : `${count} player${count === 1 ? '' : 's'}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function CandidateRow({
  candidate,
  isSelected,
  onSelect,
}: {
  candidate: ChallengeCandidate;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { player, playedBefore, lastPlayedAt, reliable, existingChallengeId } = candidate;
  const alreadyChallenged = existingChallengeId !== null;

  const history =
    playedBefore === 0
      ? 'Never played'
      : `Played ${playedBefore}×${
          lastPlayedAt ? `, last ${new Date(lastPlayedAt).toLocaleDateString()}` : ''
        }`;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      disabled={alreadyChallenged}
      onClick={onSelect}
      className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition
        disabled:cursor-not-allowed disabled:opacity-50
        ${
          isSelected
            ? 'border-emerald-500 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-950'
            : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900'
        }`}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">{player.displayName}</span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">
          {alreadyChallenged ? 'Already challenged' : history}
        </span>
      </span>
      <span className="ml-3 shrink-0 text-right">
        <span className="block text-sm tabular-nums text-slate-600 dark:text-slate-300">
          {candidate.rating}
        </span>
        {!reliable && (
          // A rating below the provisional threshold is still converging, so
          // saying "evenly matched" would overstate what we actually know.
          <span className="block text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-500">
            provisional
          </span>
        )}
      </span>
    </button>
  );
}

export function NewChallengePage() {
  const navigate = useNavigate();
  const [format] = useState<Format>('1v1');
  const [band, setBand] = useState<ChallengeBand | null>(null);
  const [opponentId, setOpponentId] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const candidates = useChallengeCandidates(format, band);
  const create = useCreateChallenge();

  async function handleSend() {
    if (opponentId === null) return;
    setError(null);

    try {
      await create.mutateAsync({
        opponentId,
        format,
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      void navigate('/challenges');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not send that challenge.');
    }
  }

  const list = candidates.data?.candidates ?? [];

  return (
    <>
      <h1 className="mb-1 text-2xl font-bold tracking-tight">Challenge someone</h1>
      <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">
        Pick how hard a game you fancy, then choose an opponent. They will get an email.
      </p>

      <div className="space-y-4">
        <Card>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            What are you after?
          </h2>
          {candidates.isPending ? (
            <p className="text-sm text-slate-400">Working out the odds…</p>
          ) : (
            <BandPicker
              buckets={candidates.data?.buckets ?? []}
              selected={band}
              onSelect={(next) => {
                setBand(next);
                // The chosen opponent may not be in the new band.
                setOpponentId(null);
              }}
            />
          )}
        </Card>

        {band !== null && (
          <Card>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Choose an opponent
            </h2>

            {candidates.isPending ? (
              <p className="text-sm text-slate-400">Loading players…</p>
            ) : list.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Nobody is in that band at the moment. Try another.
              </p>
            ) : (
              <div role="radiogroup" aria-label="Opponent" className="space-y-2">
                {list.map((candidate) => (
                  <CandidateRow
                    key={candidate.player.id}
                    candidate={candidate}
                    isSelected={opponentId === candidate.player.id}
                    onSelect={() => setOpponentId(candidate.player.id)}
                  />
                ))}
              </div>
            )}
          </Card>
        )}

        {opponentId !== null && (
          <Card className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Say something (optional)
              </span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={MAX_CHALLENGE_MESSAGE_LENGTH}
                rows={2}
                placeholder="Best of three?"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base dark:border-slate-700 dark:bg-slate-950"
              />
            </label>

            <FormError message={error} />

            <Button type="button" disabled={create.isPending} onClick={() => void handleSend()}>
              {create.isPending ? 'Sending…' : 'Send challenge'}
            </Button>

            <p className="text-center text-xs text-slate-400 dark:text-slate-500">
              If they accept, sort a time out between yourselves — then enter the result.
            </p>
          </Card>
        )}
      </div>
    </>
  );
}
